import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useOrderFulfillments } from './useOrderFulfillments';
import Card from '../../components/Card';

const formatDate = (date: Date): string =>
  date.toLocaleDateString(process.env.NODE_ENV === 'test' ? 'en' : undefined);

interface Props {
  interval: string;
  intervalCount: number;
}

const SubsOrderFulfillment: React.FC<Props> = ({ interval, intervalCount }) => {
  const { t } = useTranslation();
  const {
    isLoading,
    orderFulfillments,
    rescheduleFulfillment,
  } = useOrderFulfillments();

  const fulfilledOrders = (orderFulfillments?.fulfillmentOrders || []).filter(
    (f) => f.status === 'CLOSED',
  );
  const unfulfilledOrders = (orderFulfillments?.fulfillmentOrders || []).filter(
    (f) => f.status === 'SCHEDULED',
  );

  // Last fulfilled order or first fulfillment order
  const lastFulfilledOrder =
    fulfilledOrders[fulfilledOrders.length - 1] ||
    orderFulfillments?.fulfillmentOrders[0];
  const fulfilledAt = lastFulfilledOrder
    ? formatDate(new Date(lastFulfilledOrder.fulfillAt))
    : '-';
  const nextOrderToFulfill = unfulfilledOrders[0];
  const nextFulfillAt =
    nextOrderToFulfill && formatDate(new Date(nextOrderToFulfill.fulfillAt));
  const skipToDate =
    nextOrderToFulfill &&
    formatDate(
      dayjs(nextOrderToFulfill.fulfillAt)
        .add(intervalCount, interval as dayjs.ManipulateType)
        .toDate(),
    );

  const handleSkip = useCallback(async () => {
    const currentLastDeliveryDate = new Date(
      unfulfilledOrders[unfulfilledOrders.length - 1].fulfillAt,
    );
    const nextFulfillmentDate = dayjs(currentLastDeliveryDate)
      .add(intervalCount, interval as dayjs.ManipulateType)
      .toISOString();
    try {
      await rescheduleFulfillment(nextOrderToFulfill.id, nextFulfillmentDate);
    } catch (e) {
      console.error(e);
    }
  }, [nextOrderToFulfill, intervalCount, interval, unfulfilledOrders]);

  return (
    <>
      <Card className="bundle-card--last-fulfillment">
        <div className="bundleapp-flex">
          <div className="bundleapp-flex-1">
            <p>
              <strong>{t('tables.last_fulfillment')}</strong> <br />
              {isLoading ? t('tables.loading_fulfillments') : fulfilledAt}
            </p>
          </div>
        </div>
      </Card>
      {!isLoading && nextFulfillAt && (
        <Card className="bundle-card--next-fulfillment">
          <div className="bundleapp-flex">
            <div className="bundleapp-flex-1">
              <p>
                <strong>{t('tables.next_fulfillment')}</strong> <br />
                <>
                  {nextFulfillAt}
                  {' · '}
                  <button
                    type="button"
                    className="bundleapp-btn--text"
                    data-bundle-tip={`${t(
                      'subscription.skip_until',
                    )} ${skipToDate}`}
                    onClick={handleSkip}
                  >
                    {t('tables.skip')}
                  </button>
                </>
              </p>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default SubsOrderFulfillment;
